import {
  getCustomerDetails,
  getCustomerNameMatches
} from "../../../CustomersPage/api/services.ts"
import type { LeadFormDataCustomer } from "../components/lead-form-data.ts"
import {
  type CustomerMatchCandidate,
  isEmailMatch,
  isPhoneMatch
} from "./customer-match.ts"

interface LinkedCustomerAddress {
  address: string | null
  address_2: string | null
  city: string | null
  state: string | null
  zip_code: string | null
}

interface LinkedCustomer {
  id: string | null
  customer: LeadFormDataCustomer
  address: LinkedCustomerAddress | null
}

function customerValuesOf(
  candidate: CustomerMatchCandidate
): LeadFormDataCustomer {
  return {
    first_name: candidate.first_name ?? null,
    last_name: candidate.last_name ?? null,
    phone: candidate.phone ?? null,
    email: candidate.email ?? null
  }
}

function addressOf(
  source: Partial<LinkedCustomerAddress> | null | undefined
): LinkedCustomerAddress | null {
  const address = source?.address?.trim()
  if (!address) return null

  return {
    address,
    address_2: source?.address_2 ?? null,
    city: source?.city ?? null,
    state: source?.state ?? null,
    zip_code: source?.zip_code ?? null
  }
}

/**
 * Current autocomplete rows carry an id directly. During a rolling deploy an
 * older backend may omit it, so the fallback still reconciles through exact
 * name matches by email or phone — the same comparisons the server binds on.
 */
async function resolveCustomerId(
  candidate: CustomerMatchCandidate
): Promise<string | null> {
  if (candidate.id) return candidate.id

  const first_name = candidate.first_name?.trim() ?? ""
  const last_name = candidate.last_name?.trim() ?? ""
  if (!first_name && !last_name) return null

  const response = await getCustomerNameMatches({ first_name, last_name })
  if (!response.ok) return null

  const matches = response.data ?? []
  const byEmail = matches.filter((match) =>
    isEmailMatch(candidate.email, match.email)
  )
  const byPhone = matches.filter((match) =>
    isPhoneMatch(candidate.phone, match.phone)
  )
  const reconciled = byEmail.length ? byEmail : byPhone

  // A name that resolves to several records is not an identity; linking the
  // first would bind the lead to whichever the server happened to order first.
  return reconciled.length === 1 ? reconciled[0].id : null
}

/**
 * Turns a picked candidate into the link the create call sends. The id is what
 * links; the full record is fetched only to learn the address the "Same as
 * Customer Address" toggle needs, which neither search payload carries in full.
 */
async function resolveLinkedCustomer(
  candidate: CustomerMatchCandidate
): Promise<LinkedCustomer> {
  const customer = customerValuesOf(candidate)
  const id = await resolveCustomerId(candidate)

  if (!id) return { id: null, customer, address: addressOf(candidate) }

  const details = await getCustomerDetails(id)
  if (!details.ok || !details.data) {
    return { id, customer, address: addressOf(candidate) }
  }

  return {
    id,
    customer: {
      first_name: details.data.first_name ?? customer.first_name,
      last_name: details.data.last_name ?? customer.last_name,
      phone: details.data.phone ?? customer.phone,
      email: details.data.email ?? customer.email
    },
    address: addressOf(details.data) ?? addressOf(candidate)
  }
}

export {
  type LinkedCustomer,
  type LinkedCustomerAddress,
  addressOf,
  customerValuesOf,
  resolveCustomerId,
  resolveLinkedCustomer
}
